/**
 * <card-swatch-picker> — Saturn Squeeze
 *
 * Makes the color swatches on a product-card interactive: tapping (mobile) or
 * clicking/hovering (desktop) a swatch swaps the card's main image to that
 * color's variant photo, in place, with no page navigation and no layout shift.
 *
 * Design constraints honored:
 *  - Mobile-first: tap = swap (primary). Desktop hover = preview (nice-to-have).
 *  - No eager loading: the variant image is fetched only on the first interaction
 *    with its swatch (we set src/srcset on demand and decode() before showing).
 *  - No layout shift: we swap src/srcset on the SAME <img> (same box), and only
 *    cross-fade opacity.
 *  - Reduced motion: the fade is skipped (CSS handles it; JS still swaps).
 *  - No framework. Vanilla custom element.
 */
class CardSwatchPicker extends HTMLElement {
  connectedCallback() {
    this.buttons = Array.from(this.querySelectorAll('.card-swatches__dot'));
    if (!this.buttons.length) return;

    this.img = this._findCardImage();
    if (!this.img) return;

    // Remember the card's original image so hover-leave / reset restores it.
    this.original = {
      src: this.img.getAttribute('src') || '',
      srcset: this.img.getAttribute('srcset') || '',
    };
    this.committed = null; // a tapped swatch "sticks" until another is tapped

    // Desktop fine-pointer gets hover preview; coarse (touch) does not.
    this.finePointer =
      window.matchMedia && window.matchMedia('(pointer: fine)').matches;

    this.buttons.forEach((btn) => {
      btn.addEventListener('click', this._onClick.bind(this, btn));
      if (this.finePointer) {
        btn.addEventListener('mouseenter', () => this._preview(btn));
      }
    });

    if (this.finePointer) {
      this.addEventListener('mouseleave', () => this._resetToCommitted());
    }
  }

  _findCardImage() {
    // The swatch picker is rendered inside product-card__content, a sibling of
    // the card gallery. Walk up to the card, then grab the first gallery image.
    const card = this.closest('product-card, .product-card');
    if (!card) return null;
    const gallery = card.querySelector('[ref="cardGallery"], .card-gallery');
    const scope = gallery || card;
    // The first visible slide's <img>; fall back to any img in the gallery.
    // Slides are <slideshow-slide> custom elements; the hidden ones carry [hidden].
    return (
      scope.querySelector('slideshow-slide:not([hidden]) img') ||
      scope.querySelector('img')
    );
  }

  _onClick(btn, e) {
    // Swatch is a real swap control, not a nav link — prevent the card link.
    e.preventDefault();
    e.stopPropagation();
    this.committed = btn;
    this.buttons.forEach((b) =>
      b.setAttribute('aria-pressed', b === btn ? 'true' : 'false')
    );
    this._swap(btn);
  }

  _preview(btn) {
    // Hover preview must not disturb the committed (tapped) selection state.
    this._swap(btn);
  }

  _resetToCommitted() {
    if (this.committed) {
      this._swap(this.committed);
    } else {
      this._apply(this.original.src, this.original.srcset);
    }
  }

  async _swap(btn) {
    const src = btn.getAttribute('data-swatch-img');
    const srcset = btn.getAttribute('data-swatch-srcset');
    if (!src || !this.img) return;
    if (this.img.getAttribute('src') === src) return; // already showing

    // Decode off-screen first so the visible image never flashes a half-load.
    try {
      const pre = new Image();
      pre.src = src;
      if (srcset) pre.srcset = srcset;
      if (pre.decode) await pre.decode();
    } catch (_) {
      /* decode can reject on some browsers/aborts — fall through and swap anyway */
    }

    const reduce =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduce) {
      this._apply(src, srcset);
      return;
    }

    this.img.classList.add('is-swatch-swapping', 'is-swatch-fading');
    const finish = () => {
      this._apply(src, srcset);
      // next frame, fade back in
      requestAnimationFrame(() =>
        this.img.classList.remove('is-swatch-fading')
      );
      this.img.removeEventListener('transitionend', finish);
    };
    this.img.addEventListener('transitionend', finish);
    // Safety: if transitionend doesn't fire (e.g. display quirks), force-finish.
    setTimeout(() => {
      if (this.img.classList.contains('is-swatch-fading')) finish();
    }, 260);
  }

  _apply(src, srcset) {
    if (!this.img) return;
    this.img.setAttribute('src', src);
    if (srcset) this.img.setAttribute('srcset', srcset);
    // Drop sizes mismatch: keep the existing sizes attr; widths cover it.
  }
}

if (!customElements.get('card-swatch-picker')) {
  customElements.define('card-swatch-picker', CardSwatchPicker);
}
